/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { AttributeDataType } from '@msdyn365-commerce/retail-proxy';
import { IRefineItemData } from './common';

/**
 * Get input without formatting.
 * @param input - Input string.
 * @returns - Returns number.
 */
export function getInputWithoutFormatting(input: string): string {
    // First try to cast raw input to a number
    const inputAsNumber = Number(input);
    if (!Number.isNaN(inputAsNumber)) {
        return input;
    }

    // Otherwise try a reverse lookup and fall back to the raw input if all else fails
    // const reverseLookupResult = formattedPriceReverseLookup.get(input);
    // return reverseLookupResult || input;
    return input;
}

/**
 * Checks if the data type value corresponds to a slider.
 * @param dataTypeValue - Data type value.
 * @returns - Returns boolean.
 */
export function isRangeType(dataTypeValue: number | undefined): boolean {
    return (
        dataTypeValue === AttributeDataType.Currency ||
        dataTypeValue === AttributeDataType.Decimal ||
        dataTypeValue === AttributeDataType.Integer
    );
}

/**
 * Find the refinement criterion associated with this product refiner value.
 * @param productRefinerValue - Product refiner value to match.
 * @param refinementCriterion - Selected refinement criteria.
 * @returns - Boolean.
 */
export function isMatchingRefinementCriterion(productRefinerValue: IRefineItemData, refinementCriterion: IRefineItemData): boolean {
    // If the value is a range, then match only on data type value; otherwise match on item string
    return (
        refinementCriterion.refinerItemId === productRefinerValue.refinerItemId &&
        refinementCriterion.sourceValue === productRefinerValue.sourceValue &&
        refinementCriterion.dataTypeValue === productRefinerValue.dataTypeValue &&
        (isRangeType(refinementCriterion.dataTypeValue) || refinementCriterion.name === productRefinerValue.name)
    );
}

/**
 * Find the refinement criterion associated with this product refiner value.
 * @param productRefinerValue - Product refiner value to match.
 * @param refinementCriteria - Selected refinement criteria.
 * @returns - Returns the matching refiner.
 */
export function findMatchingRefinementCriterion(
    productRefinerValue: IRefineItemData,
    refinementCriteria: IRefineItemData[]
): IRefineItemData | undefined {
    // If the value is a range, then match only on data type value; otherwise match on item string
    return refinementCriteria.find((refinementCriterion: IRefineItemData) =>
        isMatchingRefinementCriterion(productRefinerValue, refinementCriterion)
    );
}
